import { useEffect, useState } from "react";
import {
  LogOut,
  RefreshCw,
  UtensilsCrossed,
  MapPin,
  Heart,
  MessageSquare,
  Users,
  ExternalLink,
  X,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { data } from "../lib/api";

// ---------- Shared UI ----------
const StatCard = ({ icon: Icon, label, value, accent = false }) => (
  <div
    className={`p-5 rounded-2xl border ${
      accent
        ? "bg-[#FF4F00] border-[#FF4F00] text-white"
        : "bg-white border-zinc-200"
    }`}
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon
        className={`w-4 h-4 ${accent ? "text-white" : "text-[#FF4F00]"}`}
        strokeWidth={2}
      />
      <span
        className={`font-mono text-[10px] tracking-[0.22em] uppercase ${
          accent ? "text-white/90" : "text-zinc-500"
        }`}
      >
        {label}
      </span>
    </div>
    <div className="font-display font-black text-4xl tracking-tighter">
      {value ?? 0}
    </div>
  </div>
);

const Section = ({ title, count, icon: Icon, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon className="w-4 h-4 text-[#FF4F00]" strokeWidth={2} />}
      <h2 className="font-display font-extrabold text-2xl tracking-tight text-zinc-900">
        {title}
      </h2>
      {count !== undefined && (
        <span className="chip ml-1">{count}</span>
      )}
    </div>
    {children}
  </section>
);

const EmptyRow = ({ text }) => (
  <div className="surface p-8 text-center text-zinc-400 text-sm">{text}</div>
);

// ---------- Cards ----------
const RestaurantCard = ({ r }) => (
  <div className="surface p-5 hover:shadow-sm transition-shadow" data-testid="restaurant-card">
    <div className="flex items-start justify-between mb-3">
      <UtensilsCrossed className="w-4 h-4 text-[#FF4F00]" />
      <span className={`chip ${r.source === "like" ? "chip-soft" : ""}`}>
        {r.source === "like" ? "Liked" : "Visited"}
      </span>
    </div>
    <h3 className="font-display font-bold text-lg tracking-tight text-zinc-900 mb-1">
      {r.name}
    </h3>
    {r.category && (
      <div className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">
        {r.category}
      </div>
    )}
    {(r.street || r.city || r.country) && (
      <div className="flex items-start gap-1.5 text-sm text-zinc-600">
        <MapPin className="w-3 h-3 mt-1 shrink-0" />
        <span>
          {[r.street, r.city, r.state, r.country].filter(Boolean).join(", ")}
        </span>
      </div>
    )}
    {r.latitude && r.longitude && (
      <a
        href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
        target="_blank"
        rel="noreferrer"
        className="inline-block mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#FF4F00] hover:underline"
      >
        View on Google Maps ↗
      </a>
    )}
    {r.message && r.source !== "like" && (
      <p className="text-sm text-zinc-600 mt-3 italic">"{r.message}"</p>
    )}
  </div>
);

const PlaceCard = ({ p }) => (
  <div className="surface p-5" data-testid="place-card">
    <MapPin className="w-4 h-4 text-[#FF4F00] mb-3" />
    <h3 className="font-display font-bold text-lg tracking-tight text-zinc-900 mb-1">
      {p.name}
    </h3>
    {p.category && (
      <div className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">
        {p.category}
      </div>
    )}
    <div className="text-sm text-zinc-600">
      {[p.city, p.state, p.country].filter(Boolean).join(", ") || "—"}
    </div>
    {p.message && (
      <p className="text-sm text-zinc-600 mt-3 italic line-clamp-3">"{p.message}"</p>
    )}
  </div>
);

const LikeCard = ({ l }) => (
  <div className="surface p-5 flex gap-3 items-start" data-testid="like-card">
    <Heart
      className={`w-4 h-4 mt-1 shrink-0 ${
        l.is_restaurant ? "text-[#FF4F00] fill-[#FF4F00]" : "text-zinc-400"
      }`}
    />
    <div className="min-w-0">
      <h3 className="font-display font-bold text-base text-zinc-900 truncate">
        {l.name}
      </h3>
      {l.category && (
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-zinc-500 mt-1">
          {l.category}
        </div>
      )}
    </div>
  </div>
);

const PostCard = ({ p }) => {
  const [showComments, setShowComments] = useState(false);
  const comments = p.comments || [];
  return (
    <article className="surface p-5" data-testid="post-card">
      <div className="flex items-center gap-3 mb-3 text-[11px] font-mono tracking-[0.15em] uppercase text-zinc-500">
        <span>{p.created_time ? new Date(p.created_time).toLocaleDateString() : ""}</span>
        {p.place?.name && (
          <>
            <span className="w-1 h-1 bg-zinc-300 rounded-full" />
            <span className="text-[#FF4F00] flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {p.place.name}
            </span>
          </>
        )}
      </div>
      {p.place && (p.place.city || p.place.country || p.place.street) && (
        <div className="text-xs text-zinc-500 mb-3 flex items-center gap-1.5 flex-wrap">
          <MapPin className="w-3 h-3 text-zinc-400" />
          {[p.place.street, p.place.city, p.place.state, p.place.country]
            .filter(Boolean)
            .join(" · ")}
          {p.place.latitude && p.place.longitude && (
            <a
              href={`https://www.google.com/maps?q=${p.place.latitude},${p.place.longitude}`}
              target="_blank"
              rel="noreferrer"
              className="ml-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[#FF4F00] hover:underline"
            >
              View map ↗
            </a>
          )}
        </div>
      )}
      {p.message && (
        <p className="text-zinc-800 leading-relaxed mb-3 whitespace-pre-line">
          {p.message}
        </p>
      )}
      {p.thumb && (
        <img
          src={p.thumb}
          alt=""
          className="w-full max-w-md h-48 object-cover rounded-lg border border-zinc-200"
        />
      )}
      <div className="mt-3 flex gap-4 text-xs text-zinc-500 items-center">
        {comments.length > 0 ? (
          <button
            onClick={() => setShowComments((v) => !v)}
            className="flex items-center gap-1.5 hover:text-[#FF4F00] transition-colors"
            data-testid="toggle-comments"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {comments.length} {comments.length === 1 ? "comment" : "comments"}
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase ml-1">
              {showComments ? "Hide" : "Show"}
            </span>
          </button>
        ) : (
          <span className="flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> 0 comments
          </span>
        )}
        {p.permalink_url && (
          <a
            href={p.permalink_url}
            target="_blank"
            rel="noreferrer"
            className="text-[#FF4F00] inline-flex items-center gap-1 hover:underline ml-auto"
          >
            View on Facebook <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      {showComments && comments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-zinc-200 space-y-3" data-testid="post-comments">
          {comments.map((c) => (
            <div key={c.comment_id} className="flex gap-3" data-testid="post-comment">
              {c.from_picture ? (
                <img
                  src={c.from_picture}
                  alt={c.from}
                  className="w-8 h-8 rounded-full border border-zinc-200 shrink-0"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-zinc-200 shrink-0 flex items-center justify-center text-zinc-500 font-bold text-xs">
                  {(c.from || "?").charAt(0)}
                </div>
              )}
              <div className="surface-soft px-4 py-2.5 rounded-2xl flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-0.5 flex-wrap">
                  <span className="font-display font-bold text-sm text-zinc-900">
                    {c.from || "Unknown"}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-400">
                    {c.created_time
                      ? new Date(c.created_time).toLocaleDateString()
                      : ""}
                  </span>
                </div>
                <p className="text-sm text-zinc-700 whitespace-pre-line break-words">
                  {c.message}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
};

// ---------- Friend detail modal ----------
function FriendDetailModal({ friendFbId, friendName, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("restaurants");

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoading(true);
        // Find matching user by FB id
        const { users } = await data.users();
        const u = users.find((x) => x.facebook_id === friendFbId);
        if (!u) {
          if (!abort) {
            setError("This friend has not signed into the app yet.");
            setLoading(false);
          }
          return;
        }
        const r = await data.userCategorized(u.id);
        if (!abort) {
          setD(r);
          setLoading(false);
        }
      } catch (e) {
        if (!abort) {
          setError(e?.response?.data?.detail || e.message);
          setLoading(false);
        }
      }
    })();
    return () => {
      abort = true;
    };
  }, [friendFbId]);

  const tabs = [
    { key: "restaurants", label: "Restaurants", icon: UtensilsCrossed },
    { key: "places", label: "Places", icon: MapPin },
    { key: "likes", label: "Likes", icon: Heart },
    { key: "comments", label: "Comments", icon: MessageSquare },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4 fade-in"
      onClick={onClose}
      data-testid="friend-modal"
    >
      <div
        className="bg-white rounded-2xl border border-zinc-200 max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            {d?.profile?.picture && (
              <img
                src={d.profile.picture}
                alt=""
                className="w-11 h-11 rounded-full border border-zinc-200"
              />
            )}
            <div>
              <div className="font-display font-bold text-lg">
                {d?.profile?.name || friendName}
              </div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                Friend profile
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-zinc-50"
            data-testid="modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-zinc-500 font-mono text-xs tracking-[0.2em] uppercase">
            Loading…
          </div>
        ) : error ? (
          <div className="p-10 text-center">
            <div className="chip chip-soft mb-3 inline-flex">Heads up</div>
            <p className="text-zinc-600">{error}</p>
            <p className="text-xs text-zinc-400 mt-2">
              Ask them to visit the app and click "Continue with Facebook" once.
            </p>
          </div>
        ) : d ? (
          <>
            {/* stats */}
            <div className="grid grid-cols-4 gap-3 p-5 border-b border-zinc-200 bg-zinc-50">
              {[
                { icon: UtensilsCrossed, label: "Restaurants", v: d.stats?.restaurants },
                { icon: MapPin, label: "Places", v: d.stats?.places },
                { icon: Heart, label: "Likes", v: d.stats?.likes },
                { icon: MessageSquare, label: "Comments", v: d.stats?.comments },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <s.icon className="w-4 h-4 text-[#FF4F00] mx-auto mb-1" />
                  <div className="font-display font-black text-2xl tracking-tighter">
                    {s.v ?? 0}
                  </div>
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* tabs */}
            <div className="flex gap-2 p-3 border-b border-zinc-200 overflow-x-auto">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`chip ${tab === t.key ? "chip-active" : ""}`}
                  data-testid={`friend-tab-${t.key}`}
                >
                  <t.icon className="w-3 h-3" /> {t.label}
                </button>
              ))}
            </div>

            {/* content */}
            <div className="flex-1 overflow-y-auto p-5">
              {tab === "restaurants" && (
                <div className="grid md:grid-cols-2 gap-3">
                  {(d.restaurants || []).length ? (
                    d.restaurants.map((r, i) => <RestaurantCard key={i} r={r} />)
                  ) : (
                    <EmptyRow text="No restaurants found." />
                  )}
                </div>
              )}
              {tab === "places" && (
                <div className="grid md:grid-cols-2 gap-3">
                  {(d.places || []).length ? (
                    d.places.map((p, i) => <PlaceCard key={i} p={p} />)
                  ) : (
                    <EmptyRow text="No places found." />
                  )}
                </div>
              )}
              {tab === "likes" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {(d.likes || []).length ? (
                    d.likes.slice(0, 40).map((l) => <LikeCard key={l.id} l={l} />)
                  ) : (
                    <EmptyRow text="No likes found." />
                  )}
                </div>
              )}
              {tab === "comments" && (
                <div className="space-y-3">
                  {(d.comments || []).length ? (
                    d.comments.map((c, i) => (
                      <div key={i} className="surface p-4">
                        <div className="text-xs font-mono tracking-[0.18em] uppercase text-zinc-500 mb-1.5">
                          {c.from} · {c.created_time && new Date(c.created_time).toLocaleDateString()}
                        </div>
                        <p className="text-zinc-800">{c.message}</p>
                      </div>
                    ))
                  ) : (
                    <EmptyRow text="No comments found." />
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ---------- Main Dashboard ----------
export default function Dashboard() {
  const { user, logout } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [appUsers, setAppUsers] = useState([]);
  const [tab, setTab] = useState("restaurants");
  const [friendOpen, setFriendOpen] = useState(null);

  const load = async (force = false) => {
    try {
      force ? setRefreshing(true) : setLoading(true);
      const r = await data.myCategorized(force);
      setD(r);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    data.users().then((r) => setAppUsers(r.users || [])).catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="loading">
        <div className="font-mono text-xs tracking-[0.3em] uppercase text-zinc-500">
          Loading from Facebook…
        </div>
      </div>
    );
  }

  const appUserFbIds = new Set(appUsers.map((u) => u.facebook_id));
  const friends = d?.friends || [];
  const friendsOnApp = friends.filter((f) => appUserFbIds.has(f.id));

  const tabs = [
    { key: "restaurants", label: "Restaurants", icon: UtensilsCrossed, count: d?.stats?.restaurants },
    { key: "places", label: "Places", icon: MapPin, count: d?.stats?.places },
    { key: "likes", label: "Likes", icon: Heart, count: d?.stats?.likes },
    { key: "posts", label: "Posts", icon: MessageSquare, count: d?.stats?.posts },
    { key: "comments", label: "Comments", icon: MessageSquare, count: d?.stats?.comments },
  ];

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="dashboard-page">
      {/* header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FF4F00] rounded-lg flex items-center justify-center">
              <span className="font-display font-black text-white text-sm">V</span>
            </div>
            <div>
              <div className="font-display font-extrabold tracking-tight">
                Vouchzone
              </div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                Connected
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="btn-ghost text-sm flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Syncing…" : "Refresh"}
            </button>
            <button
              onClick={logout}
              className="btn-ghost text-sm flex items-center gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {error && (
          <div
            className="surface p-5 mb-8 flex items-start gap-3"
            data-testid="error-banner"
          >
            <div className="chip chip-soft">Error</div>
            <div className="text-sm text-zinc-700 flex-1">{error}</div>
          </div>
        )}

        {d && (
          <>
            {/* Profile hero */}
            <div className="surface p-6 mb-8 flex items-center gap-5 fade-in">
              {d.profile?.picture && (
                <img
                  src={d.profile.picture}
                  alt=""
                  className="w-16 h-16 rounded-full border-2 border-[#FF4F00]"
                />
              )}
              <div className="flex-1 min-w-0">
                <h1 className="font-display font-black text-3xl tracking-tighter">
                  {d.profile?.name}
                </h1>
                <div className="text-sm text-zinc-500">
                  {d.profile?.email || ""}
                </div>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10 fade-in">
              <StatCard icon={UtensilsCrossed} label="Restaurants" value={d.stats?.restaurants} accent />
              <StatCard icon={MapPin} label="Places" value={d.stats?.places} />
              <StatCard icon={Heart} label="Likes" value={d.stats?.likes} />
              <StatCard icon={MessageSquare} label="Comments" value={d.stats?.comments} />
            </div>

            {/* Friends section — prominent */}
            <Section
              title="Friends on Vouchzone"
              count={friendsOnApp.length}
              icon={Users}
              testId="friends-section"
            >
              <p className="text-sm text-zinc-500 mb-4 -mt-2">
                Click a friend to see their restaurants, places, likes and comments.
                Friends show up here once they sign in with Facebook on this app.
              </p>

              {friends.length === 0 ? (
                <EmptyRow text="No friends from Facebook yet. When your friends log into this app, they'll appear here." />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {friends.map((f) => {
                    const onApp = appUserFbIds.has(f.id);
                    return (
                      <button
                        key={f.id}
                        onClick={() =>
                          onApp && setFriendOpen({ fbId: f.id, name: f.name })
                        }
                        disabled={!onApp}
                        className={`surface p-4 text-left flex items-center gap-3 group transition-all ${
                          onApp
                            ? "hover:border-[#FF4F00] hover:shadow-sm cursor-pointer"
                            : "opacity-60 cursor-not-allowed"
                        }`}
                        data-testid="friend-card"
                      >
                        {f.picture?.data?.url && (
                          <img
                            src={f.picture.data.url}
                            alt={f.name}
                            className="w-12 h-12 rounded-full border border-zinc-200 shrink-0"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-zinc-900 truncate">
                            {f.name}
                          </div>
                          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">
                            {onApp ? "View data" : "Not on app yet"}
                          </div>
                        </div>
                        {onApp && (
                          <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-[#FF4F00] shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* Tabs for my own data */}
            <Section title="Your data" icon={UtensilsCrossed} testId="my-data-section">
              <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`chip ${tab === t.key ? "chip-active" : ""}`}
                    data-testid={`tab-${t.key}`}
                  >
                    <t.icon className="w-3 h-3" /> {t.label} · {t.count ?? 0}
                  </button>
                ))}
              </div>

              {tab === "restaurants" && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(d.restaurants || []).length ? (
                    d.restaurants.map((r, i) => <RestaurantCard key={i} r={r} />)
                  ) : (
                    <div className="col-span-full">
                      <EmptyRow text="No restaurants found in your posts or likes." />
                    </div>
                  )}
                </div>
              )}

              {tab === "places" && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(d.places || []).length ? (
                    d.places.map((p, i) => <PlaceCard key={i} p={p} />)
                  ) : (
                    <div className="col-span-full">
                      <EmptyRow text="No geo-tagged posts found." />
                    </div>
                  )}
                </div>
              )}

              {tab === "likes" && (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {(d.likes || []).length ? (
                    d.likes.map((l) => <LikeCard key={l.id} l={l} />)
                  ) : (
                    <div className="col-span-full">
                      <EmptyRow text="No liked pages found." />
                    </div>
                  )}
                </div>
              )}

              {tab === "posts" && (
                <div className="grid md:grid-cols-2 gap-4">
                  {(d.posts || []).length ? (
                    d.posts.map((p) => <PostCard key={p.id} p={p} />)
                  ) : (
                    <div className="col-span-full">
                      <EmptyRow text="No posts found." />
                    </div>
                  )}
                </div>
              )}

              {tab === "comments" && (
                <div className="space-y-3">
                  {(d.comments || []).length ? (
                    d.comments.map((c, i) => (
                      <div key={i} className="surface p-4" data-testid="comment-row">
                        <div className="flex items-baseline gap-3 mb-1">
                          <span className="font-display font-bold text-zinc-900">
                            {c.from || "—"}
                          </span>
                          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                            {c.created_time
                              ? new Date(c.created_time).toLocaleString()
                              : ""}
                          </span>
                        </div>
                        <p className="text-zinc-700 leading-relaxed">{c.message}</p>
                      </div>
                    ))
                  ) : (
                    <EmptyRow text="No comments on your posts yet." />
                  )}
                </div>
              )}
            </Section>
          </>
        )}
      </main>

      {friendOpen && (
        <FriendDetailModal
          friendFbId={friendOpen.fbId}
          friendName={friendOpen.name}
          onClose={() => setFriendOpen(null)}
        />
      )}
    </div>
  );
}
